import type { FocusEvent, ReactNode, WheelEvent } from "react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { cn } from "@/lib/cn";

type Props = {
  /** input 要素の `type` 値 */
  type?: HTMLInputElement["type"];
  /** フォームにエラーがあるかどうか */
  error?: boolean;
  /** コンポーネントの幅 */
  width?: number | string;
  /** オートフォーカスを行うかどうか */
  autoFocus?: boolean;
  /** コンポーネント内の先頭に表示する要素 */
  prefix?: ReactNode;
  /** コンポーネント内の末尾に表示する要素 */
  suffix?: ReactNode;
  /** コンポーネント内に表示するプレースホルダー */
  placeholder?: string;
  /** prefix のクリックイベントを無視するかどうか */
  ignorePrefixClick?: boolean;
  /** border bottom を表示するかどうか */
  borderBottom?: boolean;
  /** input 要素の value */
  value?: string | number;
  /** input 要素の defaultValue */
  defaultValue?: string;
  /** 読み取り専用モードかどうか */
  readOnly?: boolean;
};

type ElementProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  keyof Props
>;

const Input = forwardRef<HTMLInputElement, Props & ElementProps>(
  (
    {
      type,
      onFocus,
      onBlur,
      autoFocus,
      prefix,
      suffix,
      className,
      width = 180,
      disabled,
      error,
      placeholder,
      ignorePrefixClick = false,
      borderBottom = false,
      value,
      defaultValue,
      readOnly,
      ...restProps
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);

    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      ref,
      () => innerRef.current,
    );

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
    };

    const handleWheel = (e: WheelEvent<HTMLInputElement>) => {
      if (type === "number") {
        (e.target as HTMLInputElement)?.blur();
      }
    };

    useEffect(() => {
      if (autoFocus && innerRef.current) {
        innerRef.current.focus();
      }
    }, [autoFocus]);

    // valueが定義されているかどうかを確認
    const isControlled = value !== undefined;

    // 制御コンポーネントとして使用する場合は value のみを設定
    // 非制御コンポーネントとして使用する場合は defaultValue のみを設定
    const normalizedValue = isControlled ? value : undefined;
    const normalizedDefaultValue =
      !isControlled && defaultValue !== undefined ? defaultValue : undefined;

    const wrapperClasses = [
      "flex items-center text-sm rounded-md shadow-2xs",
      className,
      borderBottom ? "border-b" : "border",
      disabled
        ? "pointer-events-none border-neutral-border-disabled"
        : "border-neutral-muted-border",
      error ? "border-red-500" : "border-neutral-muted-border",
    ].filter(Boolean);

    const inputClasses = [
      "w-full p-3 overflow-hidden bg-transparent py-2 leading-none rounded-md",
      "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-muted-border",
      disabled ? "bg-neutral-fill-disabled opacity-100" : "text-black",
      readOnly ? "cursor-default" : "",
    ].filter(Boolean);

    return (
      <span
        className={cn(wrapperClasses)}
        style={{ width: typeof width === "number" ? `${width}px` : width }}
        onClick={readOnly ? undefined : () => innerRef.current?.focus()}
        onKeyUp={readOnly ? undefined : () => innerRef.current?.focus()}
        role="presentation"
      >
        {prefix && (
          <span
            className="ml-2 mr-2 shrink-0"
            onClick={ignorePrefixClick ? (e) => e.stopPropagation() : undefined}
            onKeyUp={ignorePrefixClick ? (e) => e.stopPropagation() : undefined}
            onKeyDown={
              ignorePrefixClick ? (e) => e.stopPropagation() : undefined
            }
          >
            {prefix}
          </span>
        )}
        <input
          {...restProps}
          type={type}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          readOnly={readOnly}
          disabled={disabled}
          ref={innerRef as React.Ref<HTMLInputElement>}
          aria-invalid={error || undefined}
          placeholder={placeholder}
          className={cn(inputClasses)}
          value={normalizedValue}
          defaultValue={normalizedDefaultValue}
        />
        {suffix && <span className="ml-2 shrink-0">{suffix}</span>}
      </span>
    );
  },
);

Input.displayName = "Input";

export default Input;
